import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { agencySourceLabel } from "./agencyIntegrations";
import { type AgencyTargetView, hasLegacyAgencyPosting } from "./agencyTargets";
import { AgencySource, type OpenShiftRequest } from "./models";

type AgencyEditInput = Pick<OpenShiftRequest, "agency_targets" | "agency_source_name" | "agency_shift_ids" | "agency_post_delay_hours">;

/** Only evidenced live seats trigger vendor-specific consequences; selection alone is not a posting. */
export function agencyEditMessageKeys(request: AgencyEditInput): string[] {
	if (hasLegacyAgencyPosting(request)) {
		// DEFAULT: legacy intent and internal responses do not prove a live agency posting.
		if (!request.agency_shift_ids?.length) return [];
		const source = request.agency_source_name?.toLowerCase();
		// DEFAULT: an unknown historical source cannot justify vendor-specific consequences.
		if (source !== AgencySource.Nursa && source !== AgencySource.Clipboard) return [];
		return [`agency.edit.${source}.open`, ...(source === AgencySource.Nursa ? ["agency.edit.nursa.applicants"] : []), "agency.edit.booked"];
	}
	// DEFAULT: targetless, non-legacy requests carry no evidence for an agency warning.
	if (!request.agency_targets) return [];
	const messages = request.agency_targets.flatMap((target) => {
		if (target.open_count === 0) return [];
		return [
			`agency.edit.${target.agency_source}.open`,
			...(target.agency_source === AgencySource.Nursa && target.has_applicants ? ["agency.edit.nursa.applicants"] : []),
		];
	});
	if (messages.length > 0 || request.agency_targets.some((target) => target.filled_count > 0)) messages.push("agency.edit.booked");
	return messages;
}

export function agencyTargetLabel(target: AgencyTargetView, t: TFunction): string {
	return [
		agencySourceLabel(target.agency_source),
		t(`agency.multi.state.${target.state}`),
		...(target.filled_count > 0 ? [t("agency.multi.filled", { count: target.filled_count })] : []),
	].join(" · ");
}

/** Keep the planned instant, lead time and lifecycle separate: a past due time is not a successful post. */
export function agencyScheduleDescription(
	sourceLabel: string,
	leadHours: number | null,
	scheduledAt: DateTime,
	state: AgencyTargetView["state"],
	timezone: string,
	now: DateTime,
	t: TFunction,
): string {
	if (!scheduledAt.isValid || !scheduledAt.setZone(timezone).isValid) throw new Error("Invalid agency posting schedule or department timezone");
	return [
		sourceLabel,
		...(leadHours !== null ? [t("agency.multi.schedule.lead", { count: leadHours })] : []),
		t("agency.multi.scheduled.at", { time: scheduledAt.setZone(timezone).toFormat("MMM d, yyyy HH:mm ZZZZ") }),
		...(state === "pending" && scheduledAt <= now ? [t("agency.multi.schedule.awaiting")] : []),
		...(state === "failed" ? [t("agency.multi.state.failed")] : []),
	].join(" · ");
}
